import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Search, Video } from 'lucide-react';
import OnlineClassView from './OnlineClassView';

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOnlineClassDialogOpen, setIsOnlineClassDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [meetLink, setMeetLink] = useState('');
  const [activeClassId, setActiveClassId] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', schedule: 'weekly', days_of_week: [], student_ids: [] });
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    loadBatches();
    loadStudents();
  }, []);

  const loadBatches = async () => {
    try {
      const response = await axios.get(`${API}/batches`);
      setBatches(response.data);
    } catch (error) {
      console.error('Failed to load batches:', error);
      if (error.response?.status === 403) {
        toast.error('You need to be assigned to a center to manage batches', { duration: 5000 });
      } else {
        toast.error('Failed to load batches', { duration: 3000 });
      }
    }
  };

  const loadStudents = async () => {
    if (!user.center_id) {
      toast.error('You need to be assigned to a center first', { duration: 5000 });
      return;
    }
    try {
      const response = await axios.get(`${API}/users?role=student&center_id=${user.center_id}`);
      setStudents(response.data);
      setFilteredStudents(response.data);
    } catch (error) {
      toast.error('Failed to load students');
    }
  };

  // Filter students based on search term
  useEffect(() => {
    if (!studentSearchTerm) {
      setFilteredStudents(students);
      return;
    }

    const term = studentSearchTerm.toLowerCase();
    const filtered = students.filter(student => {
      const matchName = student.name?.toLowerCase().includes(term);
      const matchEmail = student.email?.toLowerCase().includes(term);
      const matchGKCode = student.gk_code?.toLowerCase().includes(term);
      const matchModule = student.module?.toLowerCase().includes(term);
      return matchName || matchEmail || matchGKCode || matchModule;
    });

    setFilteredStudents(filtered);
  }, [studentSearchTerm, students]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBatch) {
        await axios.put(`${API}/batches/${editingBatch.id}`, formData);
        toast.success('Batch updated successfully');
      } else {
        await axios.post(`${API}/batches`, formData);
        toast.success('Batch created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ name: '', description: '', schedule: 'weekly', days_of_week: [], student_ids: [] });
      setEditingBatch(null);
      loadBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (batch) => {
    setEditingBatch(batch);
    setFormData({ 
      name: batch.name, 
      description: batch.description || '', 
      schedule: batch.schedule || 'weekly',
      days_of_week: batch.days_of_week || [],
      student_ids: batch.student_ids || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;
    try {
      await axios.delete(`${API}/batches/${batchId}`);
      toast.success('Batch deleted successfully');
      loadBatches();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  };

  const handleStartOnlineClass = (batch) => {
    // Check if batch has online students
    const batchStudents = students.filter(s => batch.student_ids?.includes(s.id));
    const onlineStudents = batchStudents.filter(s => s.student_mode === 'online');
    
    if (onlineStudents.length === 0) {
      toast.error('This batch has no online students');
      return;
    }
    
    setSelectedBatch(batch);
    setMeetLink('');
    setIsOnlineClassDialogOpen(true);
  };

  const startOnlineClass = async () => {
    if (!meetLink || !meetLink.includes('meet.google.com')) {
      toast.error('Please enter a valid Google Meet link');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/online-classes`,
        {
          batch_id: selectedBatch.id,
          meet_link: meetLink
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Online class started successfully');
      setIsOnlineClassDialogOpen(false);
      setActiveClassId(response.data.id);
    } catch (error) {
      console.error('Error starting class:', error);
      toast.error('Failed to start online class');
    }
  };

  // If viewing active class, show OnlineClassView
  if (activeClassId) {
    return <OnlineClassView classId={activeClassId} onExit={() => setActiveClassId(null)} />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="batch-management">
      <Card className="p-6 bg-white shadow-lg border-0">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Batch Management</h3>
          {user.center_id ? (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="add-batch-button">
                  <Plus className="w-4 h-4" />
                  <span>Create Batch</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="batch-dialog">
              <DialogHeader>
                <DialogTitle>{editingBatch ? 'Edit Batch' : 'Create New Batch'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pb-4">
                <div>
                  <Label htmlFor="name">Batch Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="batch-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    data-testid="batch-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="schedule">Class Schedule</Label>
                  <Select value={formData.schedule} onValueChange={(value) => setFormData({ ...formData, schedule: value })}>
                    <SelectTrigger data-testid="batch-schedule-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Days of Week Selection */}
                <div>
                  <Label>Class Days (Select batch schedule days)</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <label key={day} className="flex items-center space-x-2 cursor-pointer p-2 border rounded hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.days_of_week.includes(day.toLowerCase())}
                          onChange={(e) => {
                            const dayLower = day.toLowerCase();
                            if (e.target.checked) {
                              setFormData({ ...formData, days_of_week: [...formData.days_of_week, dayLower] });
                            } else {
                              setFormData({ ...formData, days_of_week: formData.days_of_week.filter(d => d !== dayLower) });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{day.substring(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                  {formData.days_of_week.length > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {formData.days_of_week.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Select Students from Your Center (Optional)</Label>
                  <p className="text-xs text-gray-500 mb-2">You can add students later if needed</p>
                  
                  {/* Search bar for students */}
                  {students.length > 0 && (
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        type="text"
                        placeholder="Search students by name, email, GK code..."
                        value={studentSearchTerm}
                        onChange={(e) => setStudentSearchTerm(e.target.value)}
                        className="pl-9 py-1.5 text-sm"
                        data-testid="search-students-batch"
                      />
                    </div>
                  )}

                  <div className="border rounded-lg p-3 max-h-64 overflow-y-auto">
                    {students.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No students in your center</p>
                    ) : filteredStudents.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No students found</p>
                    ) : (
                      filteredStudents.map(student => (
                        <label key={student.id} className="flex items-center space-x-2 py-2 hover:bg-gray-50 px-2 rounded">
                          <input
                            type="checkbox"
                            checked={formData.student_ids.includes(student.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, student_ids: [...formData.student_ids, student.id] });
                              } else {
                                setFormData({ ...formData, student_ids: formData.student_ids.filter(id => id !== student.id) });
                              }
                            }}
                            className="rounded"
                            data-testid={`student-checkbox-${student.id}`}
                          />
                          <span className="text-sm">{student.name} - {student.email}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-batch-button">
                  {editingBatch ? 'Update' : 'Create'} Batch
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          ) : (
            <div className="text-sm text-amber-600 bg-amber-50 px-4 py-2 rounded-lg border border-amber-200">
              Please contact admin to assign you to a center
            </div>
          )}
        </div>

        {!user.center_id ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
              <Users className="w-8 h-8 text-amber-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No Center Assigned</h4>
            <p className="text-gray-600 mb-4">You need to be assigned to a center before you can manage batches.</p>
            <p className="text-sm text-gray-500">Please contact your administrator to assign you to a center.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {batches.map(batch => {
                // Count online students in this batch
                const batchStudents = students.filter(s => batch.student_ids?.includes(s.id));
                const onlineStudentsCount = batchStudents.filter(s => s.student_mode === 'online').length;
                
                return (
                <Card key={batch.id} className="p-4 border-2 hover:shadow-md transition-shadow" data-testid={`batch-${batch.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{batch.name}</h4>
                      {batch.description && <p className="text-sm text-gray-600 mb-2">{batch.description}</p>}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                          {batch.schedule}
                        </span>
                        <span className="inline-flex items-center text-xs text-gray-500">
                          <Users className="w-3 h-3 mr-1" />
                          {batch.student_ids?.length || 0} students
                        </span>
                        {onlineStudentsCount > 0 && (
                          <span className="inline-flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                            <Video className="w-3 h-3 mr-1" />
                            {onlineStudentsCount} online
                          </span>
                        )}
                      </div>
                </div>
                <div className="flex space-x-2">
                  {onlineStudentsCount > 0 ? (
                    <button
                      onClick={() => handleStartOnlineClass(batch)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded flex items-center gap-1"
                      title="Start Online Class"
                      data-testid={`start-online-class-${batch.id}`}
                    >
                      <Video className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      className="p-2 text-gray-300 cursor-not-allowed rounded"
                      title="No online students in this batch"
                      disabled
                    >
                      <Video className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(batch)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    data-testid={`edit-batch-${batch.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(batch.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    data-testid={`delete-batch-${batch.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
              );
          })}
        </div>

        {/* Start Online Class Dialog */}
        <Dialog open={isOnlineClassDialogOpen} onOpenChange={setIsOnlineClassDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start Online Class - {selectedBatch?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="meet_link">Google Meet Link</Label>
                <Input
                  id="meet_link"
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  value={meetLink}
                  onChange={(e) => setMeetLink(e.target.value)}
                />
                <p className="text-xs text-gray-600 mt-1">
                  Create a meeting in Google Meet and paste the link here
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setIsOnlineClassDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={startOnlineClass} className="flex-1 bg-green-600 hover:bg-green-700">
                  Start Class
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {batches.length === 0 && user.center_id && (
          <p className="text-gray-500 text-center py-8">No batches created yet. Create your first batch to get started!</p>
        )}
          </>
        )}
      </Card>
    </div>
  );
};

export default BatchManagement;
